import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { X, Shield, Settings } from 'lucide-react';
import OptimizedImage from '@/components/OptimizedImage';
import cookieIcon from '@/assets/cookieicon.png';
import cookieIconWebp from '@/assets/cookieicon.webp';
import voorwaardenPdf from '@/assets/Algemene-voorwaarden-Systemique.pdf';
import { initGoogleAnalytics } from '@/lib/analytics';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      // Show after a short delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie-consent', 'accepted');
    setIsVisible(false);
    initGoogleAnalytics();
  };

  const handleReject = () => {
    localStorage.setItem('cookie-consent', 'rejected');
    setIsVisible(false);
  };

  const handleCustomize = () => {
    setShowDetails(!showDetails);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 pointer-events-none">
      <div className="pointer-events-auto animate-fade-up">
        <Card className="w-full max-w-2xl bg-card/95 backdrop-blur-sm border-primary/20 shadow-2xl">
          <CardContent className="p-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-full">
                  <OptimizedImage src={cookieIcon} webpSrc={cookieIconWebp} 
                    alt="Cookie instellingen - Privacy voorkeuren Systemique" 
                    className="h-8 w-8"
                    width={32}
                    height={32}
                  />
                </div>
                <div>
                  <h3 className="text-xl font-noto-serif font-semibold text-foreground">
                    Cookievoorkeuren
                  </h3>
                  <Badge variant="secondary" className="mt-1 text-xs">
                    Privacy & Veiligheid
                  </Badge>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleReject}
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Main content */}
            <div className="mb-6">
              <p className="text-muted-foreground leading-relaxed mb-4">
                Wij gebruiken cookies om je de beste ervaring te bieden op onze website. 
                Sommige cookies zijn noodzakelijk voor de werking van de site, terwijl andere 
                ons helpen deze website en je ervaring te verbeteren.
              </p>

              {showDetails && (
                <div className="space-y-4 p-4 bg-muted/30 rounded-lg border border-border/50">
                  <div className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-foreground mb-1">Noodzakelijke cookies</h4>
                      <p className="text-sm text-muted-foreground">
                        Deze cookies zijn essentieel voor de werking van de website en kunnen niet worden uitgeschakeld.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Settings className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-foreground mb-1">Functionele cookies</h4>
                      <p className="text-sm text-muted-foreground">
                        Deze cookies verbeteren de functionaliteit van de website en je gebruikerservaring.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleAccept}
                className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
              >
                Alle cookies accepteren
              </Button>
              
              <Button
                onClick={handleCustomize}
                variant="outline"
                className="flex-1 border-primary/20 hover:bg-primary/5 hover:border-primary/30"
              >
                <Settings className="h-4 w-4 mr-2" />
                {showDetails ? 'Verberg details' : 'Aanpassen'}
              </Button>
              
              <Button
                onClick={handleReject}
                variant="ghost"
                className="flex-1 text-muted-foreground hover:text-foreground hover:bg-muted/50"
              >
                Alleen noodzakelijke
              </Button>
            </div>

            {/* Footer */}
            <div className="mt-4 pt-4 border-t border-border/50">
              <p className="text-xs text-muted-foreground text-center">
                Door verder te gaan, ga je akkoord met ons{' '}
                <a 
                  href="/privacy" 
                  className="text-primary hover:underline font-medium"
                >
                  privacybeleid
                </a>
                {' '}en{' '}
                <a 
                  href={voorwaardenPdf} 
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline font-medium"
                >
                  algemene voorwaarden (PDF)
                </a>
                .
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CookieConsent;
